import { type BeforeSendEvent, inject } from "@vercel/analytics";

/**
 * Vercel Web Analytics for the app (dashboard, storefronts, checkout).
 *
 * Cookieless pageviews — the same tool the marketing sites already use. The
 * script is served first-party from `/_vercel/insights/*` on every Vercel
 * domain, so creator custom domains are tracked too.
 *
 * Every URL is scrubbed before it leaves the browser: some app URLs carry
 * secrets (`/checkout/:clientSecret/:paymentIntentId`), buyer IDs or emails.
 */

/** Query params worth keeping — campaign attribution only. */
const KEPT_PARAMS = new Set([
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
	"ref",
]);

/**
 * Routes whose segments after the prefix are private (secrets, buyer or
 * subscription IDs). Each placeholder replaces one segment, in order.
 */
const PRIVATE_ROUTES: Record<string, string[]> = {
	checkout: ["[clientSecret]", "[paymentIntentId]"],
	portal: ["[subscriptionId]", "[subscriptionCustomerId]"],
	"verify-buyer": ["[saleId]"],
	course: ["[saleId]"],
	// Course certificates name the person they were issued to.
	certificate: ["[verifyId]"],
	credits: ["[accountId]"],
	invoice: ["[id]"],
	// Statement verification codes are bearer secrets for the statement.
	"verify-statement": ["[code]"],
	subscription: ["[id]"],
};

/** Readable sub-pages under a private prefix that must NOT be redacted. */
const PUBLIC_SUBPATHS = new Set(["success"]);

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Payment-processor style IDs and client secrets (pi_…, seti_…, …_secret_…).
const PROCESSOR_ID = /^(pi|seti|cus|sub|cs|pm|in|ch|pay)_[A-Za-z0-9]{8,}/;

const isIdLike = (segment: string): boolean =>
	OBJECT_ID.test(segment) ||
	UUID.test(segment) ||
	PROCESSOR_ID.test(segment) ||
	segment.includes("_secret_");

const scrubPath = (pathname: string): string => {
	const segments = pathname.split("/");
	// segments[0] is "" (leading slash).
	const placeholders = PRIVATE_ROUTES[segments[1] ?? ""];
	return segments
		.map((segment, index) => {
			if (index < 2 || segment === "") return segment;
			if (placeholders && index - 2 < placeholders.length) {
				if (index === 2 && PUBLIC_SUBPATHS.has(segment)) return segment;
				return placeholders[index - 2];
			}
			return isIdLike(segment) ? "[id]" : segment;
		})
		.join("/");
};

/**
 * Strip everything private from a URL: hash, non-campaign query params, and
 * secret / ID path segments. Returns the input untouched if it can't be parsed.
 */
export const scrubAnalyticsUrl = (url: string): string => {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return url;
	}
	parsed.hash = "";
	for (const key of [...parsed.searchParams.keys()]) {
		if (!KEPT_PARAMS.has(key)) parsed.searchParams.delete(key);
	}
	parsed.pathname = scrubPath(parsed.pathname);
	return parsed.toString();
};

export const beforeSend = (event: BeforeSendEvent): BeforeSendEvent => ({
	...event,
	url: scrubAnalyticsUrl(event.url),
});

/** Inject the analytics script. Never throws — analytics must not break boot. */
export const initVercelAnalytics = (): void => {
	try {
		inject({
			mode: import.meta.env.PROD ? "production" : "development",
			debug: false,
			beforeSend,
		});
	} catch {
		// Swallow: a blocked/failed analytics script is not an app error.
	}
};
